import { useEffect, useState, useMemo } from 'react';
import { get, set, del } from 'idb-keyval';
import {
  formSections,
  template as narrativeTemplate,
} from 'constants/ccmNarrative';
import { getAgeByDOB, showResult } from 'utils';
import { render } from 'mustache';
import { generateFullName } from './badgeHelpers';
import { addSpaceAfterMarks, formatTemplateValues } from './templateHelpers';

const allRequiredForm = [];
formSections.forEach((section) => {
  section.forms.forEach((form) => {
    if (form.isRequired) {
      allRequiredForm.push(form.id);
    }
  });
});

const getDmeNames = ({ groups }) => {
  if (!groups || !groups.length) return null;
  const group = groups.find((g) => g.name === 'DME');
  let result = '';
  group?.questions.forEach((q) => {
    const yes = q?.patientAnswer?.Answer?.answer === 'Yes';
    if (yes && q.question) {
      if (!result) result += q.question;
      else result += `, ${q.question}`;
    }
  });
  return result;
};
const getMedicationInfo = (...args) =>
  args.map((arg) => (arg ? `${arg} ` : '')).join('');

const getDefaultValues = ({ patient, medications, groups }) => {
  const defaultValues = {};

  if (patient?.profile?.birthDate) {
    const age = getAgeByDOB(patient.profile.birthDate);
    defaultValues.age = age;
  }
  if (patient?.profile?.gender) {
    const { gender } = patient.profile;
    defaultValues.gender = gender.charAt(0).toUpperCase() + gender.slice(1);
  }
  if (patient?.doctor) {
    const { title, firstName, middleName, lastName, profession } =
      patient.doctor;

    const doctor = generateFullName(
      title,
      firstName,
      middleName,
      lastName,
      profession,
    );
    defaultValues.doctor = doctor;
  }
  if (medications?.length) {
    defaultValues.painmeds = '';
    medications.forEach((medication) => {
      const { name, duration, dosage, frequency, route } = medication;
      const medicationInfo = getMedicationInfo(
        name,
        duration,
        dosage,
        frequency,
        route,
      );
      if (!defaultValues.painmeds) defaultValues.painmeds += medicationInfo;
      else defaultValues.painmeds += `, ${medicationInfo}`;
    });
  }
  const dmeNames = getDmeNames({ groups });
  if (dmeNames) {
    defaultValues.DME = dmeNames;
  }

  return defaultValues;
};

const getCacheId = (patientGuid) => `ccmNarrativeForm-${patientGuid}`;

const cacheValues = async (patientGuid, values) => {
  await set(getCacheId(patientGuid), values);
};

const getValuesFromCache = async (patientGuid) => get(getCacheId(patientGuid));

const useNarrativeModal = ({
  defaultValues = {},
  onSubmit,
  closeModal,
  patient,
  medications,
  groups,
  answers,
  patientGuid,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [values, setValues] = useState(defaultValues);

  useEffect(() => {
    getValuesFromCache(patientGuid).then((valuesFromCache) => {
      if (valuesFromCache) setValues(valuesFromCache);
      else
        setValues(getDefaultValues({ patient, medications, groups, answers }));
    });
  }, []);

  const formHaveError = useMemo(
    () =>
      allRequiredForm.some((formId) => {
        const value = values[formId];
        if (Array.isArray(value) && !value.length) {
          return true;
        }
        if (!value) {
          return true;
        }
        return false;
      }),
    [values],
  );

  useEffect(() => {
    cacheValues(patientGuid, values);
  }, [values]);

  const setValue = (value) => setValues((prev) => ({ ...prev, ...value }));
  const goToPrevStep = () => setCurrentStep((prev) => prev - 1);
  const goToNextStep = () => setCurrentStep((prev) => prev + 1);

  const submit = () => {
    const formattedValues = formatTemplateValues(values);
    try {
      const resultText = render(narrativeTemplate, { data: formattedValues });
      onSubmit(addSpaceAfterMarks(resultText));
      del(getCacheId(patientGuid));
      closeModal();
    } catch (error) {
      showResult(`Error:${error.toString()}`);
    }
  };
  useEffect(() => {
    const modalElement = document.querySelector('#draggable-modal');
    modalElement?.scroll({
      top: 0,
    });
  }, [currentStep]);

  return {
    values,
    currentStep,
    formHaveError,
    setValue,
    goToPrevStep,
    goToNextStep,
    submit,
  };
};

export default useNarrativeModal;
